import { useState, useCallback, useEffect } from "react";
import type { LessonData, SlideData } from "@/types/lesson";
import { markAsComplete } from "@/lib/api";

export function useLessonState(
  lesson: LessonData | null,
  options?: { skipCompletionAnalytics?: boolean }
) {
  const skipCompletionAnalytics = options?.skipCompletionAnalytics === true;
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [quizAnswered, setQuizAnswered] = useState<Record<number, boolean>>({});

  const slides = lesson?.slides ?? [];
  const total = slides.length;
  const currentSlide: SlideData | null = total > 0 ? slides[index] : null;
  const isFirst = index === 0;
  const isLast = index >= total - 1;
  const progress = total > 0 ? ((index + 1) / total) * 100 : 0;

  const goNext = useCallback(() => {
    if (index >= total - 1) {
      setFinished(true);
      const lessonId = typeof window !== "undefined" ? window.LP_LESSON_ID ?? 0 : 0;
      const courseId = typeof window !== "undefined" ? window.LP_COURSE_ID ?? 0 : 0;
      if (lessonId && courseId && !skipCompletionAnalytics) {
        markAsComplete(lessonId, courseId).catch(() => {});
      }
    } else {
      setIndex((i) => i + 1);
    }
  }, [index, total, skipCompletionAnalytics]);

  const goPrev = useCallback(() => {
    if (index > 0) setIndex((i) => i - 1);
  }, [index]);

  const goTo = useCallback((i: number) => {
    if (i >= 0 && i < total) setIndex(i);
  }, [total]);

  const setQuizDone = useCallback((slideIndex: number) => {
    setQuizAnswered((prev) => ({ ...prev, [slideIndex]: true }));
  }, []);

  const isQuizAnswered = useCallback(
    (slideIndex: number) => quizAnswered[slideIndex] === true,
    [quizAnswered]
  );

  const startOver = useCallback(() => {
    setIndex(0);
    setFinished(false);
    setQuizAnswered({});
  }, []);

  useEffect(() => {
    setIndex(0);
    setFinished(false);
    setQuizAnswered({});
  }, [lesson?.lesson_title]);

  return {
    index,
    setIndex: goTo,
    total,
    currentSlide,
    isFirst,
    isLast,
    progress,
    goNext,
    goPrev,
    finished,
    setQuizDone,
    isQuizAnswered,
    startOver,
  };
}
